import {
  Button,
  DataTable,
  DataTableSkeleton,
  NumberInput,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  TableToolbarContent,
  TextInput,
} from '@carbon/react';
import { Add } from '@carbon/icons-react';
import {
  asProblem,
  EmptyState,
  FormModal,
  hasPermission,
  problemMessage,
  useAdmin,
  useFormat,
  type RecordValues,
} from '@pos/admin-ui';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';
import { ErrorNotice } from '../common/Notices';

async function loadPoints(accountId: string) {
  const { data, error } = await hoApi.GET('/api/v1/membership/accounts/{accountId}/points', {
    params: { path: { accountId } },
  });
  if (!data) throw error;
  return data;
}

/**
 * A member account's points (ADR 0044, ADR 0124): the balance, the tier points kept apart from it, what open orders hold, the next
 * points to expire, and the ledger's latest entries with what each added to the tier and when its points expire. Points are
 * earned and redeemed by HO from the orders stores send; an adjustment here gives or takes points with the reason.
 */
export function MemberPointsTab({ account }: { account: RecordValues }) {
  const { t } = useTranslation();
  const format = useFormat();
  const { session } = useAdmin();
  const canAdjust = hasPermission(session, 'ho.members.accounts.manage');
  const accountId = String(account.id);
  const queryClient = useQueryClient();
  const key = ['ho', 'member-points', accountId];
  const points = useQuery({ queryKey: key, queryFn: () => loadPoints(accountId) });
  const [adjusting, setAdjusting] = useState<{ points: number; note: string; key: string }>();

  const adjust = useMutation({
    mutationFn: async (body: { points: number; note: string; idempotencyKey: string }) => {
      const { data, error } = await hoApi.POST('/api/v1/membership/accounts/{accountId}/points/adjust', {
        params: { path: { accountId } },
        body,
      });
      if (!data) throw error;
      return data;
    },
    onSuccess: (data) => {
      setAdjusting(undefined);
      queryClient.setQueryData(key, data);
    },
  });

  const headers = [
    { key: 'occurredAt', header: t('loyalty.points.columns.when') },
    { key: 'entryType', header: t('loyalty.points.columns.type') },
    { key: 'points', header: t('loyalty.points.columns.points') },
    { key: 'balanceAfter', header: t('loyalty.points.columns.balance') },
    { key: 'tierPoints', header: t('loyalty.points.columns.tierPoints') },
    { key: 'expiresAt', header: t('loyalty.points.columns.expiresAt') },
    { key: 'note', header: t('loyalty.points.columns.note') },
  ];
  const signed = (n: number | string) => (Number(n) === 0 ? '' : `${Number(n) > 0 ? '+' : ''}${format.number(n)}`);

  if (points.isLoading)
    return <DataTableSkeleton columnCount={headers.length} rowCount={5} headers={headers} aria-label={t('common.loading')} />;
  if (!points.data)
    return <ErrorNotice title={problemMessage(t, asProblem(points.error))} onRetry={() => void points.refetch()} />;

  const rows = points.data.entries.map((e) => ({
    id: e.id,
    occurredAt: format.dateTime(e.occurredAt),
    entryType: t(`loyalty.points.types.${e.entryType}` as I18nKey, { defaultValue: e.entryType }),
    points: signed(e.points),
    balanceAfter: format.number(e.balanceAfter),
    tierPoints: signed(e.tierPoints),
    expiresAt: format.dateTime(e.expiresAt),
    note: e.note ?? '',
  }));
  const valid = adjusting && adjusting.points !== 0 && adjusting.note.trim().length > 0;

  return (
    <div>
      <DataTable rows={rows} headers={headers}>
        {({ rows: tableRows, headers: tableHeaders, getTableProps, getHeaderProps, getRowProps }) => (
          <TableContainer
            title={t('loyalty.points.balance', { points: format.number(points.data.balance) })}
            description={[
              t('loyalty.points.tierPoints', { points: format.number(points.data.tierPoints) }),
              t('loyalty.points.reserved', { points: format.number(points.data.reserved) }),
              ...points.data.expiring
                .slice(0, 3)
                .map((x) => t('loyalty.points.expiring', { points: format.number(x.points), date: format.dateTime(x.expiresAt) })),
            ].join(' · ')}
          >
            {canAdjust && (
              <TableToolbar>
                <TableToolbarContent className="pos-table-toolbar__content">
                  <Button
                    renderIcon={Add}
                    onClick={() => setAdjusting({ points: 0, note: '', key: crypto.randomUUID() })}
                  >
                    {t('loyalty.points.adjust')}
                  </Button>
                </TableToolbarContent>
              </TableToolbar>
            )}
            {tableRows.length === 0 ? (
              <EmptyState heading={t('loyalty.points.empty')} headingLevel={3} />
            ) : (
              <Table {...getTableProps()}>
                <TableHead>
                  <TableRow>
                    {tableHeaders.map((header) => (
                      <TableHeader {...getHeaderProps({ header })} key={header.key}>
                        {header.header}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableRows.map((row) => (
                    <TableRow {...getRowProps({ row })} key={row.id}>
                      {row.cells.map((cell) => (
                        <TableCell key={cell.id}>{cell.value as string}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        )}
      </DataTable>
      {adjusting && (
        <FormModal
          title={t('loyalty.points.adjust')}
          primaryText={t('loyalty.points.adjustSave')}
          disabled={!valid}
          pending={adjust.isPending}
          error={adjust.error ? problemMessage(t, asProblem(adjust.error)) : null}
          onClose={() => setAdjusting(undefined)}
          onSubmit={() =>
            valid &&
            adjust.mutate({ points: adjusting.points, note: adjusting.note.trim(), idempotencyKey: adjusting.key })
          }
        >
          <NumberInput
            id="loyalty-adjust-points"
            label={t('loyalty.points.adjustPoints')}
            helperText={t('loyalty.points.adjustHelp')}
            value={adjusting.points}
            step={1}
            allowEmpty={false}
            onChange={(_, { value }) => setAdjusting((a) => (a ? { ...a, points: Number(value) || 0 } : a))}
          />
          <TextInput
            id="loyalty-adjust-note"
            labelText={t('loyalty.points.adjustNote')}
            value={adjusting.note}
            maxLength={200}
            onChange={(e) => setAdjusting((a) => (a ? { ...a, note: e.target.value } : a))}
          />
        </FormModal>
      )}
    </div>
  );
}
